import React, {useEffect, useMemo, useReducer} from 'react';
import {ControlGroup, InputGroup, FormGroup} from "@blueprintjs/core";
import {useSelector} from "react-redux";

import {ContentCard, ContentToolbar} from '../components';
import {createDefaultContent} from './utils';
import {useCreateBlock, useExecuteRequest, useInitBlock, useUpdateBlockContentJson, useGetResult} from '../hooks';
import {BaseBlock, ContentActions, ContentOptions} from '../types';
import {defaultContentActions, defaultContentOptions} from '../constants';
import {initialExternalFileContentState, ExternalFileContentReducer} from "./store";
import {setInit, setContentForm, setContentFormKeyValue, unsetPreview, setPreview} from './store/actions';
import Result from './Result';
import {ExportOptions} from './ExportOptions';
import {getBlockSelector} from "../../../store/reducers/blockReducer";
import {MAX_FILE_SIZE_MB, SUPPORTED_FORMATS} from './constants';

import './ExternalFile.scss';

interface ExternalFileProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const ExternalFile = (({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: ExternalFileProps) => {
  const [contentState, contentDispatch] = useReducer(ExternalFileContentReducer, initialExternalFileContentState);

  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const uid = content.uid;
  const block = useSelector(getBlockSelector(uid as string));

  const {result} = useGetResult(uid, content.result_id, options.loadResult);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  const {executing, executeRequest} = useExecuteRequest(uid);

  useCreateBlock(content, createDefaultContent());
  useInitBlock(uid, content, contentDispatch);

  useEffect(() => {
    /*
     * Initial load on edit
     */
    if (uid) {
      contentDispatch(setContentForm(content));
    } else if (!uid && !!content.from_block_id) {
      /*
       * Comes from saved block
       */
      contentDispatch(setContentForm(content));
    } else {
      contentDispatch(setInit(true));
    }
  }, []);

  const onExecute = async () => {
    const block: any = {
      content_json: {
        url: contentState.form.url
      }
    };
    executeRequest(block);
  };

  const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    updateBlockContentJson('url', value);
    contentDispatch(setContentFormKeyValue('url', value));
  };

  const onPreview = (data: any) => {
    if (!data) {
      contentDispatch(unsetPreview());
    } else {
      try {
        const jsonData = JSON.parse(data);

        contentDispatch(setPreview(jsonData));
      } catch (e) {
        console.error(e);
      }
    }
  };

  const onExportOptionsChange = (key: string) => (value: any) => {
    updateBlockContentJson(`export_options.${key}`, value);
  }

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={options.readOnly}
      noContentElement={undefined}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
      showFooter={options.showFooter}
    >
      <div className="content__external-file">
        <ContentToolbar
          show={options?.showToolbar}
          contentId={uid as string}
          executing={executing}
          onExecute={options.showRun ? onExecute : null}
          updating={updating}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showPreview={options.showPreview}
          onPreview={onPreview}
          showSave={false}
          disabled={options.readOnly || false}
          showSelect={false}
        />
        <div className="content-card__content__form">
          <ControlGroup className="method-url-group">
            <FormGroup
              helperText={`Maximum file size: ${MAX_FILE_SIZE_MB}MB. Supported formats: ${SUPPORTED_FORMATS.map(fmt => fmt.toUpperCase()).join(', ')}.`}
              label="File URL"
              labelFor="external-file-url"
              labelInfo="(required)"
            >
              <InputGroup
                value={contentState.form.url ?? ""}
                onChange={onValueChange}
                placeholder={"Enter URL"}
                fill={true}
                disabled={contentState.isPreview || options.readOnly}
              />
            </FormGroup>
          </ControlGroup>
          {
            options.showResult && (
              <Result
                key={content.type + '_' + content.id}
                uid={uid}
                result={result}
              />
            )
          }
        </div>
      </div>
      <ExportOptions
        show={options.showExportOptions as boolean && contentState.form.integration_id !== null}
        block={block}
        onChange={onExportOptionsChange}
        disabled={options.readOnly}
        showEmbed={false}
        placeholder={'filename'}
      />
    </ContentCard>
  );
});
